import { useState, useEffect, useRef } from "react";
import { ArrowRight, CheckCircle } from "lucide-react";
import freshdropImg from "../../assets/freshdropimage.png";
import dakar360Img from "../../assets/dakar360.png";
import HeroImage from "../../assets/heroimage.jpg";

const projects = [
  {
    id: "freshdrop",
    title: "FreshDrop",
    status: "completed",
    role: "Solo Developer",
    timeline: "3 months",
    tagline: "Multi-vendor e-commerce platform with M-Pesa integration",
    description:
      "A full-stack e-commerce platform built for the Kenyan market — featuring M-Pesa payment integration, real-time order tracking, and a complete vendor management system.",
    image: freshdropImg,
    techStack: [
      "Next.js",
      "TypeScript",
      "Tailwind CSS",
      "Redux",
      "AdonisJS",
      "MySQL",
    ],
    features: [
      "Multi-vendor system - multiple stores can run their businesses in one place",
      "Complete shopping experience - browse products, add to cart, place orders, real-time tracking",
      "M-Pesa payment integration using Daraja API (STK push, transaction responses)",
      "Role-based access (customers, providers, staff, admin)",
      "Product management with categories and subcategories",
      "Order management with proper cancellation rules",
      "Analytics dashboard for vendors",
    ],
  },
  {
    id: "dakar360",
    title: "Dakar360",
    status: "ongoing",
    role: "Collaborator",
    timeline: "In Development",
    tagline: "Integrated business management solution with eTIMS compliance",
    description:
      "An integrated business management solution for growing businesses. From sales and inventory to accounting and compliance - all in one platform, accessible via web or mobile.",
    image: dakar360Img,
    techStack: [
      "Next.js",
      "Tailwind CSS",
      "Redux",
      "Laravel",
      "PostgreSQL",
      "Google Cloud AI",
    ],
    features: [
      "Point of Sale - fast, intuitive POS that works online and offline",
      "Inventory Management - track stock levels, manage suppliers, optimize warehouse",
      "Sales & Invoicing - create professional invoices, manage customer accounts, track payments",
      "Expense Tracking - real-time financial reports",
      "eTIMS Integration - fully compliant with KRA regulations, auto-transmit electronic tax invoices",
      "Multi-Business Support - manage multiple businesses from one account",
    ],
  },
];

function ProjectCard({ project, index }) {
  const [expanded, setExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={cardRef}
      className={`border border-gray-200 rounded-2xl overflow-hidden bg-white hover:shadow-lg transition-all duration-500 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      }`}
      style={{ transitionDelay: `${index * 150}ms` }}
    >
      {/* Image */}
      <div className="relative h-56 overflow-hidden bg-gray-100">
        <img
          src={project.image}
          alt={project.title}
          className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
        />
        <span
          className={`absolute top-3 right-3 text-xs font-medium px-2.5 py-1 rounded-full ${
            project.status === "completed"
              ? "bg-black text-white"
              : "bg-blue-600 text-white"
          }`}
        >
          {project.status === "completed" ? "Completed" : "In progress"}
        </span>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="flex items-start justify-between mb-2">
          <h2 className="text-2xl font-bold text-black">{project.title}</h2>
          <div className="text-right">
            <span className="text-xs text-gray-500 block">{project.role}</span>
            <span className="text-xs text-gray-400">{project.timeline}</span>
          </div>
        </div>
        <p className="text-blue-600 text-sm font-medium mb-3">
          {project.tagline}
        </p>
        <p className="text-gray-600 text-sm leading-relaxed mb-4">
          {project.description}
        </p>
        <div className="mb-4">
          <div className="flex flex-wrap gap-1.5">
            {project.techStack.map((tech, i) => (
              <span
                key={i}
                className="text-xs px-2 py-1 rounded-full bg-gray-50 text-gray-600 border border-gray-200"
              >
                {tech}
              </span>
            ))}
          </div>
        </div>

        {expanded && (
          <div className="mt-4 pt-4 border-t border-gray-100 animate-fadeIn">
            <h3 className="text-sm font-semibold text-black mb-3">
              What the system does:
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {project.features.map((feature, i) => (
                <div key={i} className="flex items-start gap-2">
                  <CheckCircle
                    size={14}
                    className="text-blue-600 mt-0.5 flex-shrink-0"
                  />
                  <span className="text-sm text-gray-600">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
        >
          {expanded ? "See less" : "See more"}
          <ArrowRight
            size={14}
            className={`transition-transform duration-300 ${
              expanded ? "rotate-90" : ""
            }`}
          />
        </button>
      </div>
    </div>
  );
}

export default function ProjectsSection() {
  const [pageVisible, setPageVisible] = useState(false);
  const headerRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setPageVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  return (
    <main className="bg-white overflow-hidden">
      {/* ── HERO SECTION ── */}
      <div
        className="relative border-b border-gray-100"
        style={{
          backgroundImage: `url(${HeroImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-14">
          <div
            className={`text-center transition-all duration-1000 ease-out ${
              pageVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-8"
            }`}
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 leading-tight tracking-tight">
              Featured <span className="text-blue-400">projects</span>
            </h1>
            <p
              className={`text-gray-200 text-sm sm:text-base md:text-lg leading-relaxed max-w-2xl mx-auto transition-all duration-1000 ease-out delay-150 ${
                pageVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-6"
              }`}
            >
              Real products built end-to-end from database design to the final
              product.
            </p>
          </div>
        </div>
      </div>

      {/* ── CONTENT BELOW HERO ── */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-16">
        {/* My work label */}
        <div
          ref={headerRef}
          className={`text-center mb-12 transition-all duration-700 ease-out ${
            pageVisible
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-8"
          }`}
        >
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 px-3 py-1 rounded-full">
              <span className="w-1 h-1 rounded-full bg-blue-600"></span>
              <span className="text-blue-700 text-xs font-semibold tracking-wide">
                My work
              </span>
            </div>
          </div>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 gap-8">
          {projects.map((project, idx) => (
            <ProjectCard key={project.id} project={project} index={idx} />
          ))}
        </div>
      </div>

      {/* Animation keyframes */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.4s ease-out;
        }
      `}</style>
    </main>
  );
}
